'use client'

import { BagCheck } from 'solar-icons'

import {
  BarcodeBadge,
  Button,
  ButtonSkeleton,
  KeyChip,
  PriceBadge,
  Skeleton,
  SkuBadge,
  Thumbnail,
  ThumbnailSkeleton,
} from '@/components/ui'

interface SearchCardProps extends React.ComponentPropsWithoutRef<'div'> {
  product: Product
}

export const SearchCard = ({ product, ...restProps }: SearchCardProps) => {
  return (
    <div
      className="flex gap-4 rounded-lg border border-gray-100 p-3 shadow-card focus-within:bg-gray-50 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-800 lg:items-center dark:border-gray-700"
      {...restProps}
    >
      <Thumbnail size="sm" className="shrink-0" src={product.image_url} />

      <div className="flex w-full flex-col justify-between gap-4 md:flex-row">
        <div className="meta flex flex-grow flex-col gap-1">
          <h2 className="font-medium text-blue-600">
            <span>{product.variant_label ?? product.title}</span>
          </h2>

          <div className="flex flex-col gap-1 lg:flex-row lg:items-center lg:gap-5">
            <PriceBadge
              price={product?.default_price?.amount}
              badge={{ variant: 'default' }}
            />
            <SkuBadge>{product.sku}</SkuBadge>
            <BarcodeBadge>{product?.default_price?.barcode}</BarcodeBadge>
          </div>
        </div>

        <div className="actions hidden items-center gap-2 md:flex">
          <Button variant="outline" className="gap-2 pr-2 shadow-card">
            <BagCheck className="size-5" strokeWidth={1.5} />
            <span>Add to Cart</span>
            <KeyChip variant="default" shortcut="enter" className="h-full" />
          </Button>
        </div>
      </div>
    </div>
  )
}

export const SearchCardSkeleton = () => {
  return (
    <div className="flex gap-4 rounded-lg border border-gray-100 p-3 shadow-card md:items-center dark:border-gray-700">
      <ThumbnailSkeleton />
      <div className="flex w-full flex-col gap-2 md:flex-row md:justify-between lg:gap-0">
        <div className="flex w-full flex-col gap-2 lg:flex-grow">
          <Skeleton className="flex h-4 w-[80%] rounded-full" />
          <div className="flex w-full flex-col gap-5 lg:flex-row lg:items-center">
            <div className="flex w-[120px] items-center gap-2">
              <Skeleton className="h-3 w-[80px] rounded-full" />
              <Skeleton className="h-3 w-[200px] rounded-full" />
            </div>
            <div className="flex w-[120px] items-center gap-2">
              <Skeleton className="h-3 w-[80px] rounded-full" />
              <Skeleton className="h-3 w-[200px] rounded-full" />
            </div>
            <div className="flex w-[120px] items-center gap-2">
              <Skeleton className="h-3 w-[80px] rounded-full" />
              <Skeleton className="h-3 w-[200px] rounded-full" />
            </div>
          </div>
        </div>
        <div className="hidden items-center gap-2 md:flex">
          <ButtonSkeleton />
        </div>
      </div>
    </div>
  )
}
